import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { LogOut, Settings, User, ChevronDown } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useDisplayName } from '@/hooks/useDisplayName'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/utils'

interface Props {
  /** Compact mode: show only avatar (for collapsed sidebar) */
  compact?: boolean
  /** Direzione di apertura del dropdown: 'up' per la sidebar (default), 'down' per la topbar mobile */
  placement?: 'up' | 'down'
}

export function UserMenu({ compact = false, placement = 'up' }: Props) {
  const { user, logout }  = useAuth()
  const { name, photoUrl } = useDisplayName()
  const navigate          = useNavigate()
  const [open, setOpen]   = useState(false)
  const menuRef           = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  function handleLogout() {
    setOpen(false)
    logout()
    navigate(ROUTES.LOGIN)
  }

  const initials = (name || user?.email || 'U')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const settingsRoute =
    user?.role === 'OPERATOR' ? ROUTES.OPERATOR.SETTINGS :
    user?.role === 'ADMIN'    ? ROUTES.ADMIN.DASHBOARD :
    ROUTES.USER.PROFILE

  return (
    <div ref={menuRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-white/10',
          compact && 'justify-center'
        )}
      >
        {/* Avatar */}
        {photoUrl ? (
          <img src={photoUrl} alt="" className="h-8 w-8 flex-shrink-0 rounded-full object-cover" />
        ) : (
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-holly-500 text-xs font-bold text-white">
            {initials}
          </div>
        )}

        {!compact && (
          <>
            <div className="min-w-0 text-left">
              <p className="max-w-[120px] truncate text-sm font-medium text-gray-900">
                {name || 'Utente'}
              </p>
              <p className="max-w-[120px] truncate text-xs text-muted-foreground">
                {user?.email}
              </p>
            </div>
            <ChevronDown className={cn('h-4 w-4 flex-shrink-0 text-gray-400 transition-transform', open && 'rotate-180')} />
          </>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className={cn(
          'absolute z-50 min-w-[200px] rounded-xl border bg-white py-1 shadow-lg',
          placement === 'down'
            ? 'right-0 top-full mt-1'
            : compact ? 'bottom-0 left-12' : 'bottom-full left-0 mb-1'
        )}>
          {/* User info header */}
          <div className="border-b px-4 py-3">
            <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            <span className={cn(
              'mt-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              user?.role === 'ADMIN'    ? 'bg-red-100 text-red-700' :
              user?.role === 'OPERATOR' ? 'bg-holly-100 text-holly-700' :
              'bg-gray-100 text-gray-600'
            )}>
              {user?.role === 'ADMIN' ? 'Admin' : user?.role === 'OPERATOR' ? 'Operatore' : 'Utente'}
            </span>
          </div>

          {/* Links */}
          <div className="py-1">
            <Link
              to={settingsRoute}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <User className="h-4 w-4 text-gray-400" />
              Il mio account
            </Link>
            <Link
              to={settingsRoute}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Settings className="h-4 w-4 text-gray-400" />
              Impostazioni
            </Link>
          </div>

          {/* Logout */}
          <div className="border-t pt-1">
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" />
              Esci dall'account
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
